import { mergeAttributes, Node } from "@tiptap/core"

import { enhanceDocumentPreviews } from "../../document-preview/enhance"

export interface FileEmbedOptions {
	HTMLAttributes: Record<string, unknown>
}

interface SetFileEmbedOptions {
	src: string
	fileName: string
	mimeType?: string | null
	fileSize?: number | null
}

declare module "@tiptap/core" {
	interface Commands<ReturnType> {
		fileEmbed: {
			setFileEmbed: (options: SetFileEmbedOptions) => ReturnType
		}
	}
}

/**
 * A document attached to an article or changelog. The node stores only what the
 * reader side needs to fetch and name the file; the preview itself is built in
 * the browser, so no third party ever sees the document.
 */
export const FileEmbedExtension = Node.create<FileEmbedOptions>({
	name: "fileEmbed",
	group: "block",
	atom: true,
	draggable: true,
	selectable: true,

	addOptions() {
		return { HTMLAttributes: {} }
	},

	addAttributes() {
		return {
			src: { default: null },
			fileName: {
				default: null,
				parseHTML: (element) => element.getAttribute("data-file-name"),
				renderHTML: (attributes) =>
					attributes.fileName
						? { "data-file-name": String(attributes.fileName) }
						: {},
			},
			mimeType: {
				default: null,
				parseHTML: (element) => element.getAttribute("data-file-type"),
				renderHTML: (attributes) =>
					attributes.mimeType
						? { "data-file-type": String(attributes.mimeType) }
						: {},
			},
			fileSize: {
				default: null,
				parseHTML: (element) => {
					const raw = element.getAttribute("data-file-size")
					return raw ? Number(raw) : null
				},
				renderHTML: (attributes) =>
					attributes.fileSize
						? { "data-file-size": String(attributes.fileSize) }
						: {},
			},
		}
	},

	parseHTML() {
		return [{ tag: "div[data-file-embed]" }]
	},

	addCommands() {
		return {
			setFileEmbed:
				(options: SetFileEmbedOptions) =>
				({ commands }) => {
					if (!options.src || !options.fileName) return false
					return commands.insertContent({ type: this.name, attrs: options })
				},
		}
	},

	/**
	 * The editor builds the same preview the reader gets, so an author sees the
	 * spreadsheet they attached rather than a bare link. `ignoreMutation` keeps
	 * ProseMirror from reading the preview back as a document edit, which would
	 * corrupt the article.
	 */
	addNodeView() {
		return ({ node }) => {
			const dom = document.createElement("div")
			dom.setAttribute("data-file-embed", "")
			dom.setAttribute("data-file-src", String(node.attrs.src ?? ""))
			dom.setAttribute("data-file-name", String(node.attrs.fileName ?? ""))
			if (node.attrs.mimeType) {
				dom.setAttribute("data-file-type", String(node.attrs.mimeType))
			}
			if (node.attrs.fileSize) {
				dom.setAttribute("data-file-size", String(node.attrs.fileSize))
			}
			dom.contentEditable = "false"

			const holder = document.createElement("div")
			holder.append(dom)
			const stop = enhanceDocumentPreviews(holder)

			return {
				dom,
				ignoreMutation: () => true,
				destroy: () => stop(),
			}
		}
	},

	renderHTML({ HTMLAttributes, node }) {
		const src = String(node.attrs.src ?? "")
		const fileName = String(node.attrs.fileName ?? "Document")

		return [
			"div",
			mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
				"data-file-embed": "",
				"data-file-src": src,
			}),
			[
				"a",
				{ href: src, target: "_blank", rel: "noopener noreferrer" },
				fileName,
			],
		]
	},
})
